import React, { useState, useEffect, useCallback } from 'react';
import { payoutAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';
import { CSVLink } from 'react-csv';

const AdminPayouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');

  const isWithdrawalRequest = (request) => {
    if (request?.requestType) {
      return request.requestType === 'WITHDRAWAL';
    }

    const transactionId = request?.transactionId || '';
    const remarks = (request?.remarks || '').toLowerCase();

    if (transactionId.startsWith('WALLET-TOPUP-') || transactionId.startsWith('PURCHASE-')) {
      return false;
    }

    if (remarks.includes('wallet top-up') || remarks.includes('purchase payment')) {
      return false;
    }

    return true;
  };

  const fetchPayouts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await payoutAPI.getAllWithdrawalRequests({ status: filterStatus });

      if (response?.data?.success) {
        let filteredPayouts = (response.data.withdrawalRequests || []).filter(isWithdrawalRequest);

        // Apply month and year filters
        if (filterMonth || filterYear) {
          filteredPayouts = filteredPayouts.filter(payout => {
            if (!payout.requestedAt) return false;

            const requestDate = new Date(payout.requestedAt);
            const requestMonth = String(requestDate.getMonth() + 1).padStart(2, '0');
            const requestYear  = String(requestDate.getFullYear());

            const monthMatch = !filterMonth || requestMonth === filterMonth;
            const yearMatch  = !filterYear  || requestYear  === filterYear;

            return monthMatch && yearMatch;
          });
        }

        setPayouts(filteredPayouts);
      } else {
        setPayouts([]);
      }
    } catch (err) {
      setError('Failed to load withdrawal requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterMonth, filterYear]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  // FIX: Accept requestId (_id of the subdocument) instead of requestIndex
  const handleApprove = async (userId, requestId) => {
    // FIX: prompt() returns a string — capture it directly, never destructure it
    const transactionId = prompt('Enter transaction ID (optional):') || undefined;

    try {
      const response = await payoutAPI.approveWithdrawal(userId, requestId, {
        transactionId,
        remarks: 'Approved by admin'
      });

      if (response?.data?.success) {
        await fetchPayouts();
      } else {
        setError(response?.data?.error || 'Failed to approve withdrawal');
      }
    } catch (err) {
      setError('Failed to approve withdrawal');
      console.error(err);
    }
  };

  // FIX: Accept requestId (_id of the subdocument) instead of requestIndex
  const handleReject = async (userId, requestId) => {
    // FIX: prompt() returns a string — capture it directly, never destructure it
    const remarks = prompt('Enter rejection reason:') || 'Rejected by admin';

    try {
      const response = await payoutAPI.rejectWithdrawal(userId, requestId, { remarks });

      if (response?.data?.success) {
        await fetchPayouts();
      } else {
        setError(response?.data?.error || 'Failed to reject withdrawal');
      }
    } catch (err) {
      setError('Failed to reject withdrawal');
      console.error(err);
    }
  };

  // Prepare CSV data
  const prepareCSVData = () => {
    return payouts.map((payout, index) => ({
      'SR No': index + 1,
      'Name': payout.userName || 'Unknown User',
      'Amount (Rs)': payout.amount || 0,
      'Account Number': payout.bankDetails?.accountNumber || 'N/A',
      'IFSC Code': payout.bankDetails?.ifscCode || 'N/A',
    }));
  };

  // Generate filename based on filters
  const generateFileName = () => {
    const status = filterStatus || 'ALL';
    const month  = filterMonth
      ? new Date(`${filterYear || new Date().getFullYear()}-${filterMonth}-01`)
          .toLocaleString('default', { month: 'short' })
      : '';
    const year = filterYear || new Date().getFullYear();
    const date = new Date().toISOString().split('T')[0];

    let filename = `withdrawal_requests_${status}_${year}`;
    if (month) filename += `_${month}`;
    filename += `_${date}.csv`;

    return filename;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] p-6 rounded-2xl">

      {/* Header */}
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-[#0F2F3D]">
          Withdrawal Management
        </h2>
        <p className="text-gray-500 mt-1">
          Manage user withdrawal requests and approvals
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 space-y-4 lg:space-y-0">
        <div className="flex space-x-4">
          {['PENDING', 'APPROVED', 'REJECTED'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-[#E4572E] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {status} ({payouts.filter(p => p.status === status).length})
            </button>
          ))}
        </div>

        {/* Date Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Month:</label>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E] focus:border-transparent"
            >
              <option value="">All Months</option>
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Year:</label>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E] focus:border-transparent"
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <button
            onClick={() => { setFilterMonth(''); setFilterYear(''); }}
            className="px-3 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Withdrawal Requests Table */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-[#0F2F3D] mb-0">
              Withdrawal Requests ({payouts.filter(p => p.status === filterStatus).length})
            </h3>
            {payouts.length > 0 && (
              <CSVLink
                data={prepareCSVData()}
                filename={generateFileName()}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>Export CSV</span>
              </CSVLink>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Table Header */}
            <thead className="bg-[#0F2F3D] text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">SR. No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">User</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Amount</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Bank Details</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">UPI Details</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">QR Code</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Requested</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Action</th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-gray-200">
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-400">
                    No withdrawal requests found.
                  </td>
                </tr>
              ) : (
                payouts.map((payout, index) => (
                  <tr key={payout._id || index} className="hover:bg-[#E4572E]/5 transition-all">
                    <td className="px-6 py-4 font-medium text-[#0F2F3D]">{index + 1}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-[#0F2F3D]">
                        {payout.userName || 'Unknown User'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {payout.userEmail || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-bold text-[#E4572E]">
                      Rs. {payout.amount?.toLocaleString()}
                      <div className="text-xs text-gray-500">
                        Net: Rs. {payout.netAmount?.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="space-y-1">
                        <div><strong>A/C:</strong> {payout.bankDetails?.accountNumber || 'N/A'}</div>
                        <div><strong>Name:</strong> {payout.bankDetails?.accountHolder || 'N/A'}</div>
                        <div><strong>Bank:</strong> {payout.bankDetails?.bankName || 'N/A'}</div>
                        <div><strong>Branch:</strong> {payout.bankDetails?.branchName || 'N/A'}</div>
                        <div><strong>IFSC:</strong> {payout.bankDetails?.ifscCode || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="space-y-1">
                        <div><strong>UPI No:</strong> {payout.kycDetails?.upiNumber || 'N/A'}</div>
                        <div><strong>UPI ID:</strong> {payout.kycDetails?.upiId || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {payout.kycDetails?.qrCode ? (
                        <div className="flex items-center space-x-2">
                          <img
                            src={payout.kycDetails.qrCode}
                            alt="QR Code"
                            className="w-12 h-12 object-cover border border-gray-300 rounded cursor-pointer hover:scale-110 transition-transform"
                            onClick={() => window.open(payout.kycDetails.qrCode, '_blank')}
                          />
                          <div className="text-xs">
                            <div className="text-green-600 font-medium">✓ Available</div>
                            <div className="text-gray-500">Click to view</div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center mx-auto mb-1">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <div className="text-xs text-red-600 font-medium">Not Uploaded</div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        payout.status === 'APPROVED'
                          ? 'bg-green-100 text-green-800'
                          : payout.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {payout.requestedAt
                        ? new Date(payout.requestedAt).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {payout.status === 'PENDING' && (
                        <div className="flex space-x-2">
                          {/* FIX: pass payout._id (subdocument _id) instead of payout.requestIndex */}
                          <button
                            onClick={() => handleApprove(payout.userId, payout._id)}
                            className="px-3 py-1 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(payout.userId, payout._id)}
                            className="px-3 py-1 text-sm border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPayouts;